import { LearnFlashcardShell } from "@-/dev.improve.haniverse.components.ui.learn-card-shell";
import { Platform, StyleSheet, View } from "@-/com.npmjs.react-native";
import Animated, {
  useAnimatedStyle,
  useDerivedValue,
  useSharedValue,
  withTiming,
} from "@-/com.npmjs.react-native-reanimated";
import { learnCardPresentationKey } from "@-/dev.improve.haniverse.lib.learn.card-action";
import {
  learnCardMotionStyle,
  type LearnCardFace,
  type LearnCardViewMode,
  type LearnDeckRenderSlot,
  type LearnDeckSurface,
} from "@-/dev.improve.haniverse.lib.learn.deck";
import type { LearnDeckStageValues } from "@-/dev.improve.haniverse.routes.nav.app.learn.deck.context";
import { pagedSuccessorOffset } from "@-/dev.improve.haniverse.routes.nav.app.learn._lib.card-gestures";
import {
  type ReactNode,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useRouteBodyState } from "@-/dev.improve.haniverse.lib.navigation.route-body-state";
import { useDeviceSetting } from "@-/dev.improve.haniverse.lib.device-settings";

import { LearnFlashcardBinderTabs } from "#learn-card-binder-tabs";
import { LearnFlashcardSurfaceProvider } from "#learn-card-surface-provider";
import { LearnFlashcardInfoSurface, LearnFlashcardStudySurface } from "#learn-card-view-surface";
import {
  MorphemeInfoTabHostContext,
  MorphemeMnemonicsTabHostContext,
} from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.panels.tab-host-context";

const styles = StyleSheet.create({
  fill: StyleSheet.absoluteFill,
  hidden: { opacity: 0 },
});

const CARD_SHADOW_PLATFORM = Platform.OS;

export type LearnFlashcardStageProps = {
  back: ReactNode;
  /** The deck's exploration of this card, covering its surfaces while set. */
  exploration?: ReactNode;
  face: LearnCardFace;
  front: ReactNode;
  info: ReactNode;
  /** Tab activation is independent of which prepared presentation owns the stage. */
  isActive?: boolean;
  /** Block card actions while a rating settles, without disabling the parent pan. */
  isInteractionPending: boolean;
  onSelectView: (view: LearnCardViewMode) => void;
  onSurfaceReady: (presentationKey: string, surface: LearnDeckSurface) => void;
  /** The deck's surface budget controls mounting, independently of visual activity. */
  preloadBack?: boolean;
  /**
   * `departing`: the card a rating promoted past, drawn (never interactive) on the face and view
   * it was rated on while it flies off above the new current card. `previous`: the same card once
   * its flight has landed, kept mounted (and hidden) so Undo can bring it straight back.
   */
  role: "current" | "departing" | "previous" | "successor";
  slot: LearnDeckRenderSlot;
  stage: LearnDeckStageValues;
  view: LearnCardViewMode;
};

/**
 * One card on the stage: a body holding the study surface (front over back) under the Info
 * surface. Both layers stay mounted in a fixed order once visited. Info mounts on its first
 * visit, then stays mounted so returning preserves its selected section and scroll. Prepared
 * cards need only front and back.
 *
 * The binder tabs that switch between Study and Info are part of the faces themselves: the back
 * and Info each draw their own strip in their own shell, above their body, so the tabs share
 * every transform, fade and mount of the face they belong to. The unrevealed front has none.
 * Nothing else positions or animates tabs, so they can never be drawn apart from their face. The
 * back's mnemonics sections teleport into a host inside that same back face.
 *
 * A link opened from the card (`exploration`) covers the current card's surfaces with the
 * referenced panels and a Back to Review pill, which a swipe presses too; the card itself never
 * leaves the stage.
 *
 * The successor is visible underneath during drags, but hidden during a reveal crossfade.
 * Touches reach only the current card's visible surface and face;
 * everything else is `pointerEvents="none"` and hidden from accessibility.
 */
export function LearnFlashcardStage({
  back,
  exploration,
  face,
  front,
  info,
  isActive = true,
  isInteractionPending,
  onSelectView,
  onSurfaceReady,
  preloadBack = true,
  role,
  slot,
  stage,
  view,
}: LearnFlashcardStageProps) {
  const presentationKey = learnCardPresentationKey(slot.presentation);
  const isCurrent = role === "current";
  const isDeparting = role === "departing" || role === "previous";
  const isActiveCurrent = isCurrent && isActive;
  const isExploring = exploration != null;
  const isInteractive = isActiveCurrent && !isInteractionPending;
  const isStudyInteractive = isInteractive && !isExploring;
  const [hasVisitedInfo, setHasVisitedInfo] = useRouteBodyState(
    "card-visited-info",
    view === "info",
  );
  const [isPronunciationKeyboardVisible] = useDeviceSetting("isPronunciationKeyboardVisible");
  // Glyph reviews say how to reveal at the bottom of the card, where an open pinyin keyboard
  // sits instead.
  const reviewDrillKind = slot.item.kind === "review" ? slot.item.drillType.kind : null;
  const showRevealHint =
    reviewDrillKind === "meaning" ||
    (reviewDrillKind === "pronunciation" && !isPronunciationKeyboardVisible);
  const mountedRef = useRef(true);
  useLayoutEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);
  useEffect(() => {
    if (isDeparting) {
      // Flying off as it was rated, Info included.
      return;
    }
    if (!isActiveCurrent) {
      // A later return to Study must not eagerly rebuild an old, hidden Info panel.
      setHasVisitedInfo(false);
    } else if (view === "info") {
      setHasVisitedInfo(true);
    }
  }, [isActiveCurrent, isDeparting, setHasVisitedInfo, view]);
  const infoTabHost = useMemo(
    () => ({
      hostName: `learn-card-info-tabs-${presentationKey}`,
      isActive: isActiveCurrent && view === "info" && !isExploring,
      isInteractive,
    }),
    [isActiveCurrent, isInteractive, isExploring, presentationKey, view],
  );
  const mnemonicsTabHostName = `learn-card-mnemonics-tabs-${presentationKey}`;
  const isBackInteractive = isStudyInteractive && view === "study" && face === "back";
  // Mnemonics elsewhere on the card (Info, the front) know they are on a Learn card, but only the
  // back hosts its sections, in its own Learn tab. They stay there whenever the back is mounted, on
  // every card in every role, hidden with it and drawn with it.
  const mnemonicsTabHost = useMemo(
    () => ({ hostName: mnemonicsTabHostName, isActive: false, isInteractive: false }),
    [mnemonicsTabHostName],
  );
  const backMnemonicsTabHost = useMemo(
    () => ({ hostName: mnemonicsTabHostName, isActive: true, isInteractive: isBackInteractive }),
    [isBackInteractive, mnemonicsTabHostName],
  );
  // One measurement serves every face's binder tabs, so a face drawn for the first time (Info)
  // never waits on a layout pass of its own to draw them.
  const [cardWidth, setCardWidth] = useState(0);
  const shadowProgress = useSharedValue(0);
  useEffect(() => {
    if (isDeparting) {
      return;
    }
    shadowProgress.set(withTiming(isCurrent ? 1 : 0, { duration: isCurrent ? 220 : 0 }));
  }, [isCurrent, isDeparting, shadowProgress]);
  // Shadow the alpha silhouette of the entire transparent group: body plus binder tabs.
  // A rectangular boxShadow would fill the tab gap and leave a seam along the body bottom.
  const shadowStyle = useAnimatedStyle(() => {
    const opacity =
      slot.item.kind !== "slide" && (isCurrent || isDeparting) ? shadowProgress.get() * 0.16 : 0;
    if (CARD_SHADOW_PLATFORM === "ios") {
      return {
        shadowColor: "#000000",
        shadowOffset: { height: 2, width: 0 },
        shadowOpacity: opacity,
        shadowRadius: 3,
      };
    }
    if (CARD_SHADOW_PLATFORM === "web") {
      return { filter: `drop-shadow(0px 2px 3px rgba(0, 0, 0, ${opacity}))` };
    }
    return {
      filter: [
        {
          dropShadow: {
            color: `rgba(0, 0, 0, ${opacity})`,
            offsetX: 0,
            offsetY: 2,
            standardDeviation: 3,
          },
        },
      ],
    };
  });

  const handleReady = useCallback(
    (surface: LearnDeckSurface) => {
      if (mountedRef.current) {
        onSurfaceReady(presentationKey, surface);
      }
    },
    [onSurfaceReady, presentationKey],
  );

  const cardStyle = useAnimatedStyle(() => {
    const departure = stage.departure.get();
    const motionPresentation = stage.motionPresentation.get();
    const motion = learnCardMotionStyle({
      departure,
      departureProgress: stage.departureProgress.get(),
      motionPresentation,
      // A slide card pages on to its successor, which waits beside it rather than under it.
      pagedSuccessorOffset: pagedSuccessorOffset(stage, motionPresentation.current),
      presentationKey,
      successorPresentation: stage.successorPresentation.get(),
      tilt: stage.tilt.get(),
      x: stage.dragX.get(),
      y: stage.dragY.get(),
    });
    const revealProgress = stage.flipProgress.get();
    if (
      motionPresentation.current !== presentationKey &&
      departure?.presentation !== presentationKey &&
      revealProgress > 0 &&
      revealProgress < 1
    ) {
      // Prepared cards must not show through the current card's reveal crossfade.
      return { ...motion, opacity: 0 };
    }
    return motion;
  });
  // Whether this card's front is on show, decided where the uncovering itself is decided.
  const frontVisibleSignal = useDerivedValue(
    () =>
      !isExploring &&
      view === "study" &&
      face === "front" &&
      (stage.motionPresentation.get().current === presentationKey ||
        (stage.successorPresentation.get() === presentationKey && stage.exitProgress.get() > 0)),
  );

  const card = (
    <Animated.View
      accessibilityElementsHidden={!isActiveCurrent}
      // Every face ends at the stage's 9pt inset. Binder tabs stay inside that footprint.
      className="absolute inset-0"
      collapsable={false}
      importantForAccessibility={isActiveCurrent ? "auto" : "no-hide-descendants"}
      onLayout={({ nativeEvent: { layout } }) => {
        setCardWidth(layout.width);
      }}
      pointerEvents={isActiveCurrent ? "box-none" : "none"}
      style={[cardStyle, shadowStyle]}
      testID={
        isCurrent
          ? "learn-card-current"
          : role === "departing"
            ? "learn-card-departing"
            : role === "previous"
              ? "learn-card-previous"
              : "learn-card-successor"
      }
    >
      <View className="min-h-0 flex-1" pointerEvents="box-none">
        <View
          accessibilityElementsHidden={isExploring || view !== "study"}
          importantForAccessibility={
            !isExploring && view === "study" ? "auto" : "no-hide-descendants"
          }
          pointerEvents={isStudyInteractive && view === "study" ? "box-none" : "none"}
          // Info, when shown, covers the study surface with its own body and tabs.
          style={[styles.fill, isActiveCurrent && view === "info" ? styles.hidden : undefined]}
        >
          <LearnFlashcardStudySurface
            back={
              back !== null && preloadBack ? (
                <MorphemeMnemonicsTabHostContext value={backMnemonicsTabHost}>
                  <LearnFlashcardSurfaceProvider
                    isActive={
                      !isExploring && isActiveCurrent && view === "study" && face === "back"
                    }
                    isCurrent={isCurrent}
                    onReady={handleReady}
                    presentationKey={presentationKey}
                    surface="back"
                  >
                    {back}
                  </LearnFlashcardSurfaceProvider>
                </MorphemeMnemonicsTabHostContext>
              ) : null
            }
            backBinderTabs={
              <LearnFlashcardBinderTabs
                isInteractive={isBackInteractive}
                mnemonicsTabHostName={mnemonicsTabHostName}
                onSelect={onSelectView}
                view="study"
                width={cardWidth}
              />
            }
            face={face}
            front={
              <LearnFlashcardSurfaceProvider
                frontVisibleSignal={frontVisibleSignal}
                isActive={!isExploring && isActiveCurrent && view === "study" && face === "front"}
                isCurrent={isCurrent}
                onReady={handleReady}
                presentationKey={presentationKey}
                surface="front"
              >
                {front}
              </LearnFlashcardSurfaceProvider>
            }
            isInteractive={isStudyInteractive && view === "study"}
            isLesson={slot.item.kind !== "review"}
            isSlideshow={slot.item.kind === "slide"}
            presentationKey={presentationKey}
            showRevealHint={showRevealHint}
            stage={stage}
          />
        </View>
        <View
          accessibilityElementsHidden={isExploring || view !== "info"}
          importantForAccessibility={
            !isExploring && view === "info" ? "auto" : "no-hide-descendants"
          }
          pointerEvents={isStudyInteractive && view === "info" ? "box-none" : "none"}
          style={[styles.fill, view === "info" ? undefined : styles.hidden]}
        >
          {/* Optional detail panels are not part of the off-tab three-surface budget.
              Their scalar drafts remain in the card's RouteBodyStateProvider. */}
          {isActiveCurrent && (view === "info" || hasVisitedInfo) ? (
            <LearnFlashcardInfoSurface
              binderTabs={
                <LearnFlashcardBinderTabs
                  isInteractive={isStudyInteractive && view === "info"}
                  onSelect={onSelectView}
                  view="info"
                  width={cardWidth}
                />
              }
              isInteractive={isStudyInteractive && view === "info"}
            >
              <LearnFlashcardSurfaceProvider
                isActive={!isExploring && isActiveCurrent && view === "info"}
                isCurrent={isCurrent}
                onReady={handleReady}
                presentationKey={presentationKey}
                surface="info"
              >
                {info}
              </LearnFlashcardSurfaceProvider>
            </LearnFlashcardInfoSurface>
          ) : null}
        </View>
      </View>
      {isActiveCurrent && isExploring ? (
        <LearnFlashcardShell
          className="absolute inset-0"
          pointerEvents={isInteractive ? "auto" : "none"}
          testID="learn-card-exploration"
        >
          <MorphemeInfoTabHostContext value={null}>
            <MorphemeMnemonicsTabHostContext value={null}>
              <LearnFlashcardSurfaceProvider
                isActive={isActiveCurrent}
                isCurrent={isCurrent}
                onReady={handleReady}
                presentationKey={presentationKey}
                surface="info"
              >
                {exploration}
              </LearnFlashcardSurfaceProvider>
            </MorphemeMnemonicsTabHostContext>
          </MorphemeInfoTabHostContext>
        </LearnFlashcardShell>
      ) : null}
    </Animated.View>
  );

  return (
    <MorphemeInfoTabHostContext value={infoTabHost}>
      <MorphemeMnemonicsTabHostContext value={mnemonicsTabHost}>
        {card}
      </MorphemeMnemonicsTabHostContext>
    </MorphemeInfoTabHostContext>
  );
}
